import React, { ChangeEventHandler, useState } from 'react';

import { Box, PasswordInput, PasswordInputProps, Popover, Progress, Space, Text } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  MAX_PASSWORD_STRENGTH,
  MEDIUM_PASSWORD_STRENGTH,
  PASSWORD_REQUIREMENTS,
  getPasswordStrength
} from 'shared/utils/form-validators';

interface PasswordInputWithStrengthProps {
  passwordInputProps: Omit<PasswordInputProps, 'value' | 'onChange'> & {
    value: string;
    onChange: ChangeEventHandler<HTMLInputElement>;
  };
  confirmPasswordInputProps?: Omit<PasswordInputProps, 'value' | 'onChange'> & {
    value: string;
    onChange: ChangeEventHandler<HTMLInputElement>;
  };
  showLabels?: boolean;
  newPassword?: boolean;
  disabled?: boolean;
}

export function PasswordInputWithStrength({
  passwordInputProps,
  confirmPasswordInputProps,
  showLabels = true,
  newPassword = true,
  disabled = false
}: PasswordInputWithStrengthProps) {
  const { t } = useTranslation();
  const [passwordPopoverOpened, setPasswordPopoverOpened] = useState(false);

  const strength = getPasswordStrength(passwordInputProps.value);

  const passwordStrengthHintColor = (requirement: { regex: RegExp; errorMessage: string }) =>
    requirement.regex.test(passwordInputProps.value) ? 'teal' : 'red';
  const passwordStrengthProgressColor =
    strength === MAX_PASSWORD_STRENGTH ? 'teal' : strength > MEDIUM_PASSWORD_STRENGTH ? 'yellow' : 'red';

  return (
    <>
      <Popover opened={passwordPopoverOpened} position="bottom" width="target">
        <Popover.Target>
          <Box
            onFocusCapture={() => setPasswordPopoverOpened(true)}
            onBlurCapture={() => setPasswordPopoverOpened(false)}>
            <PasswordInput
              disabled={disabled}
              label={showLabels ? t(`AUTH.${newPassword ? 'NEW_' : ''}PASSWORD.LABEL`) : null}
              placeholder={t(`AUTH.${newPassword ? 'NEW_' : ''}PASSWORD.PLACEHOLDER`)}
              {...passwordInputProps}
            />
          </Box>
        </Popover.Target>
        <Popover.Dropdown>
          <Progress color={passwordStrengthProgressColor} value={strength} size="md" style={{ marginBottom: 10 }} />
          {PASSWORD_REQUIREMENTS.map((requirement, index) => (
            <Text
              key={index}
              color={passwordStrengthHintColor(requirement)}
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}
              mt={7}
              size="sm">
              {requirement.regex.test(passwordInputProps.value) ? <IconCheck size={14} /> : <IconX size={14} />}{' '}
              <Box ml={10}>{requirement.errorMessage}</Box>
            </Text>
          ))}
        </Popover.Dropdown>
      </Popover>
      {confirmPasswordInputProps && (
        <>
          <Space h={15} />
          <PasswordInput
            disabled={disabled}
            label={showLabels ? t(`AUTH.CONFIRM${newPassword ? '_NEW_' : '_'}PASSWORD.LABEL`) : null}
            placeholder={t(`AUTH.CONFIRM${newPassword ? '_NEW_' : '_'}PASSWORD.PLACEHOLDER`)}
            {...confirmPasswordInputProps}
          />
        </>
      )}
    </>
  );
}
